<template>
  <Panel
    class="panel-sidebar-builder-schema-object"
  >
    <template #header>
      <div class="panel-header-content">
        <i class="fas fa-cogs mr-2" />
        <h4>{{ t('myShopData.syncs.settingHeader') }}</h4>
      </div>
    </template>

    <div class="configuration-wrapper">
      <div class="form-field-group">
        <BaseTextInput
          :model-value="settings.name"
          :disabled="isEditMode"
          :label="t('myShopData.custom_objects.form.table_name')"
          field-id="customObjectName"
          :disable="isEditMode"
          type="text"
          @update:model-value="updateName"
        />
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidationErrors"
          field="name"
          :silent="false"
        />
      </div>

      <div class="form-field-group">
        <BaseTextarea
          :model-value="settings.description"
          :disabled="isEditMode"
          :display-emojis="false"
          :display-variables="false"
          :max-length-textarea="500"
          :label="t('myShopData.custom_objects.form.table_description')"
          :watch-props="true"
          @update:model-value="updateDescription"
        />
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidationErrors"
          field="description"
          :silent="false"
        />
      </div>
    </div>
  </Panel>
</template>

<script lang="ts">
import {
  defineComponent, computed, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Panel from 'primevue/panel';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import BaseTextarea from '@/components/fields/BaseTextarea.vue';
import Tooltip from 'primevue/tooltip';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';

export default defineComponent({
  name: 'SchemaSettingsPanel',
  components: {
    Panel,
    BaseTextInput,
    BaseTextarea,
    FieldErrors,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    settingPanelValidationErrors: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  setup(props: {settingPanelValidationErrors: any }) {
    const { t } = useI18n();
    const store = useStore();
    // get settings from store
    const settings = computed(() => store.getters['customObject/getSettings']);
    const isEditMode = computed(() => store.getters['customObject/isEditMode']);

    const formValidationErrors = ref({});
    const componentFieldErrorsKey = ref(0);
    watch(() => props.settingPanelValidationErrors, () => {
      formValidationErrors.value = props.settingPanelValidationErrors;
      console.log('formValidationErrors', formValidationErrors.value);
      componentFieldErrorsKey.value += 1;
    });

    // update settings in store
    const updateSettings = (payload: Partial<{ name: string; description: string }>) => {
      store.commit('customObject/updateSettings', payload);
    };

    const updateName = (value: string) => {
      updateSettings({ name: value });
    };

    const updateDescription = (value: string) => {
      updateSettings({ description: value });
    };

    return {
      t,
      settings,
      isEditMode,
      updateName,
      updateDescription,
      formValidationErrors,
      componentFieldErrorsKey,
    };
  },
});
</script>

<style lang="scss">
.p-panel .p-panel-header {
  border: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}
</style>

<style lang="scss" scoped>
.panel-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.icons-header-panel {
  cursor: pointer;
  color: #6c757d; /* Gris pour l'icône */
  padding: 5px;
  &:hover {
    color: #343a40;
  }
}

.configuration-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px; /* Espace entre les champs */
  padding-top: 10px;
}

.form-group {
  /* Styles pour le groupe de formulaire si besoin */
}

.field-label {
  /* Styles pour les labels si besoin */
  font-weight: 500;
  margin-bottom: 5px; /* Repris du code original */
}

/* Styles pour BaseTextInput et BaseTextarea si nécessaire */
/* :deep() peut être utile pour cibler les enfants */
:deep(.p-inputtext),
:deep(.p-textarea) {
  width: 100%; /* Assurer la pleine largeur */
}

.form-field-group {
  margin-bottom: 1rem;
}

.validation-error-message {
  display: block;
  color: var(--p-red-500, #EF4444);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.mt-1 {
    margin-top: 0.25rem; /* Petite marge en haut */
}
</style>
